import { CiReport } from '@cibo/core'
import { useSize } from '@cibo/ui/src/hooks/useSize'
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import * as d3 from 'd3'
import { flatten } from 'ramda'
import { Fragment, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

type HistoricalCarbonIntensityProps = {
  height: number
  reportData: CiReport
  commodity: string
}

const PADDING = {
  left: 56,
  top: 16,
  right: 8,
  bottom: 32,
}

const SERIES_CROPS: ('corn' | 'soybeans')[] = ['corn', 'soybeans']

export const HistoricalCarbonIntensity = ({
  reportData,
  height,
}: HistoricalCarbonIntensityProps) => {
  const { t } = useTranslation('impact/HistoricalCarbonIntensity')
  const theme = useTheme()

  const graphId = useMemo(() => `graph-${uuid()}`, [])
  const sizeTarget = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const size = useSize(sizeTarget, 100)

  const { width } = size || {}

  const innerWidth = width ? width - PADDING.left - PADDING.right : 0
  const innerHeight = height - PADDING.bottom - PADDING.top

  const yearSums: Record<string, Record<string, number>> = {}

  SERIES_CROPS.forEach(crop => {
    const cropObject: Record<string, number> = {}
    reportData.forEach(yearData => {
      if (!!yearData[crop]) cropObject[yearData.year] = yearData[crop]
    })
    if (!!Object.values(cropObject).length) yearSums[crop] = cropObject
  })

  const allValues = flatten(
    Object.values(yearSums).map(valuesByYear => Object.values(valuesByYear))
  )

  const allYears = reportData.map(a => a.year)

  const xScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([(d3.min(allYears) || 0) - 0.5, (d3.max(allYears) || 0) + 0.5])
        .range([0, innerWidth]),
    [allYears]
  )

  const yScale = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([Math.min(0, d3.min(allValues) || 0), d3.max(allValues) || 0 * 1.1])
        .range([innerHeight, 0]),
    [allValues]
  )

  const colorScale = d3.scaleOrdinal(d3.schemePaired)

  useEffect(() => {
    if (!xScale || !yScale || !svgRef.current) return

    const svg = d3.select(svgRef.current)

    svg
      .selectAll<SVGGElement, any>('g.x.axis')
      .attr('transform', `translate(0,${innerHeight})`)
      .attr('stroke-opacity', 0.1)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(allYears)
          .tickFormat(d => `${d}`)
          .tickSizeOuter(0)
      )

    svg
      .selectAll<SVGGElement, any>('g.y.axis')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(4)
          .tickSizeInner(0)
      )
      .call(g => g.select('.domain').remove())
      .call(g => {
        g.selectAll('.grid').remove()
        g.selectAll('.tick line')
          .clone()
          .attr('x2', innerWidth)
          .attr('stroke-opacity', 0.1)
          .attr('class', 'grid')
      })
  }, [xScale, yScale, width])

  const pathData = d3
    .line<{ year: number; value: number }>()
    .x((d: any) => xScale(d.year))
    .y((d: any) => yScale(d.value))

  return (
    <Box
      ref={sizeTarget}
      sx={{
        'svg text': {
          fontSize: 12,
          fontWeight: 400,
          fill: theme.palette.text.secondary,
        },
        'g.axis path': {
          stroke: theme.palette.text.secondary,
        },
      }}
    >
      <Stack direction="row" spacing={2} justifyContent="end" mr={2}>
        {Object.keys(yearSums).map((commodity, i) => (
          <Stack direction="row" alignItems="center" spacing={1} key={commodity}>
            <Box height={2} width={12} bgcolor={colorScale(commodity)} />
            <Typography variant="caption">{t(commodity)}</Typography>
          </Stack>
        ))}
      </Stack>

      <svg ref={svgRef} id={graphId} width={width || undefined} height={height}>
        <text
          textAnchor="middle"
          transform={`translate(20,${(height - PADDING.bottom) / 2} ) rotate(-90)`}
        >
          {t('yAxisLabel')}
        </text>

        <g transform={`translate(${PADDING.left},${PADDING.top})`}>
          <g className="y axis"></g>
          <g className="x axis"></g>
          <g className={`${graphId}-inner`}>
            {Object.entries(yearSums).map(([commodity, sums], i) => (
              <Fragment key={commodity}>
                <path
                  d={
                    pathData(
                      Object.entries(sums).map(([year, value]) => ({
                        year: Number(year),
                        value,
                      }))
                    ) || undefined
                  }
                  fill="none"
                  stroke={colorScale(commodity)}
                  strokeWidth={1.5}
                />

                {Object.entries(sums).map(([year, value]) => (
                  <Tooltip key={year} title={`${commodity} ${value?.toPrecision(3)}`} arrow={false}>
                    <circle
                      cx={xScale(Number(year))}
                      cy={yScale(value)}
                      r={10}
                      fill={'#00000000'}
                    />
                  </Tooltip>
                ))}
              </Fragment>
            ))}
          </g>
        </g>
      </svg>
    </Box>
  )
}
